import type { BoardGateKind } from './boardModel';
import type { DeliveryFlowWire } from './boardWire';

import type { BoardDay } from './boardModel';

export type FlowExecutor = DeliveryFlowWire['actions'][number]['executor'];

// Literal keys: `t()` reads these by lookup, which the i18n source-coverage
// scan cannot follow, so `__tests__/boardLabels.test.ts` pins them.
export const EXECUTOR_LABEL = {
  operator: 'deliveryFlow.executor.operator',
  onfleet: 'deliveryFlow.executor.onfleet',
  driver: 'deliveryFlow.executor.driver',
} as const satisfies Record<FlowExecutor, string>;

/**
 * The tenant's local time for a gate (e.g. "18:00"), read from the flow the
 * server sends. `null` when the flow does not declare that gate.
 */
export function flowGateTime(flow: DeliveryFlowWire, kind: BoardGateKind): string | null {
  return flow.gates.find((gate) => gate.kind === kind)?.local_time ?? null;
}

/** Minutes since midnight of an `HH:mm` wall time. */
export function minutesOfDay(hhmm: string): number {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Whether packaging ends strictly before dispatch does (#2631). Both cutoffs
 * are tenant settings with no ordering rule: only then is "packaging–dispatch"
 * a real span to show.
 */
export function hasDispatchWindow(packaging: string, dispatch: string): boolean {
  return minutesOfDay(packaging) < minutesOfDay(dispatch);
}

/**
 * Without a dispatch window: is the dispatch cutoff still ahead (#2631)? The
 * next gate is the earliest one not yet reached, so while the cutoff is ahead
 * the next gate sits at or before it; once it is past, every gate left is later.
 * A next gate whose instant has already passed is stale board data: not ahead.
 */
export function dispatchCutoffAhead(
  nextGate: BoardDay['nextGate'],
  dispatch: string,
  nowMs: number,
): boolean {
  return (
    nextGate !== null &&
    Date.parse(nextGate.atIso) > nowMs &&
    minutesOfDay(nextGate.localTime) <= minutesOfDay(dispatch)
  );
}

/**
 * The outside system that runs the flow: the first executor that is neither
 * the operator nor a driver. `null` when the flow has none.
 */
export function flowProvider(flow: DeliveryFlowWire): FlowExecutor | null {
  const action = flow.actions.find(
    (candidate) => candidate.executor !== 'operator' && candidate.executor !== 'driver',
  );
  return action?.executor ?? null;
}
